import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const alertVariants = cva(
  'relative w-full rounded-2xl border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>.alert-icon]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 has-[>.alert-icon]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current [&>.alert-icon]:translate-y-0.5',
  {
    variants: {
      variant: {
        default:
          'dark:bg-blue-1000 bg-grey-200 dark:border-grey-700 border-grey-400 text-blue-1000 dark:text-blue-100',
        destructive:
          'dark:bg-blue-1000 bg-red-100 dark:border-red-700 border-red-600 text-red-600 dark:text-red-400 [&>svg]:text-current [&>.alert-icon]:text-current *:data-[slot=alert-description]:text-red-600/90 dark:*:data-[slot=alert-description]:text-red-400/90',
        warning:
          'bg-yellow-200 bg-opacity-50 border-yellow-400 text-yellow-800 [&>svg]:text-current [&>.alert-icon]:text-current *:data-[slot=alert-description]:text-yellow-800/90',
        info: 'bg-blue-200 font-bold  border-blue-400 text-blue-800 dark:bg-blue-900! dark:bg-opacity-50 dark:border-blue-600 dark:text-blue-200 [&>svg]:text-current [&>.alert-icon]:text-current *:data-[slot=alert-description]:text-blue-800/90 dark:*:data-[slot=alert-description]:text-blue-200/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Alert({
  className,
  variant,
  icon,
  children,
  ...props
}: React.ComponentProps<'div'> &
  VariantProps<typeof alertVariants> & {
    icon?: string;
  }) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className, 'flex gap-3')}
      {...props}
    >
      {icon && <span className="mt-1 text-base alert-icon material-symbols-outlined">{icon}</span>}
      <div className="max-w-2xl">{children}</div>
    </div>
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 text-xl font-medium tracking-tight ',
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'max-w-2xl text-grey-1000 dark:text-blue-100 col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
